<template>
  <div class="h-full flex flex-col">
    <BaseTable
      table-id="field-mappings"
      :headers="headers"
      :list="fieldMappings"
      :selected-item="selectedFieldMapping"
      is-saving-layout-hidden
      is-search-hidden
      is-refresh-button-hidden
      is-download-button-hidden
      hide-grouping
      clickable
      :is-add-new-hidden="mode === 'view'"
      :edit-mode="mode === 'edit'"
      :add-button-text="$t('representative.fieldMapping.addNew')"
      disable-multiselect
      @add-new="onFieldMappingAdd"
      @tableItemClicked="onRowClick">
      <template #custom="{ header, item }">
        <div class="flex items-center">
          <!-- Target Field - BaseSearchDropdown -->
          <BaseSearchDropdown
            v-if="header === 'TargetField'"
            class="min-w-48"
            :key="`targetField-${item.TargetField || 'new'}-${dropdownForceUpdate}`"
            :list-items="getFilteredAvailableEntityFields(item)"
            :count="getAvailableEntityFields(item).length"
            no-results-text="No fields available"
            v-model="item.selectedTargetFieldItem"
            @item-selected="updateProperty(item, 'TargetField', $event.value)"
            @clear-selection="updateProperty(item, 'TargetField', '')"
            @update-search="updateFieldSearch" />

          <!-- Primary FieldMapping properties - BaseInput -->
          <div v-else-if="header === 'DataType'">
            <BaseSelectDropdown
              :items="dropDownDataTypes"
              :selected-item="item.selectedDataType"
              name="dataType"
              :disable="mode === 'view'"
              @update:selected="handleDataTypeSelection($event, item)" />
          </div>
          <BaseInput
            v-else-if="
              header === 'DefaultValue' || header === 'Description' || header === 'ChildRecordType'
            "
            class="h-8 bg-white rounded-md"
            color="light"
            :model-value="item[header]"
            :disabled="mode === 'view' || item.TargetField === 'DocumentHeaderRowId'"
            @update:model-value="updateProperty(item, header, $event)" />

          <!-- Boolean properties - BaseToggle -->
          <BaseToggle
            v-else-if="header === 'IsRequired' || header === 'IsNullable'"
            class="flex-col"
            :name="`${header}-${item.TargetField || 'new'}`"
            :model-value="item[header]"
            :disable="mode === 'view' || item.TargetField === 'DocumentHeaderRowId'"
            @update:model-value="updateProperty(item, header, $event)" />

          <!-- MappingField properties based on mappingType -->
          <!-- CSV properties -->
          <BaseInput
            v-else-if="
              mappingType === MappingType.CSV &&
              (header === 'ColumnName' || header === 'ColumnIndex')
            "
            class="h-8 bg-white rounded-md"
            color="light"
            :model-value="item.MappingFields[0]?.CSV[header]"
            :disabled="
              mode === 'view' ||
              item.IsChildCollection ||
              item.TargetField === 'DocumentHeaderRowId'
            "
            @update:model-value="updateProperty(item, `MappingFields.0.CSV.${header}`, $event)" />

          <!-- FixedLength properties -->
          <BaseInput
            v-else-if="
              mappingType === MappingType.FixedLength &&
              (header === 'StartPosition' || header === 'Length')
            "
            class="h-8 bg-white rounded-md"
            color="light"
            :model-value="item.MappingFields[0]?.FixedLength[header]"
            :disabled="
              mode === 'view' ||
              item.IsChildCollection ||
              item.TargetField === 'DocumentHeaderRowId'
            "
            @update:model-value="
              updateProperty(item, `MappingFields.0.FixedLength.${header}`, $event)
            " />

          <!-- JSON properties -->
          <BaseInput
            v-else-if="mappingType === MappingType.JSON && header === 'JsonPath'"
            class="h-8 bg-white rounded-md"
            color="light"
            :model-value="item.MappingFields[0]?.JSON.JsonPath"
            :disabled="
              mode === 'view' ||
              item.IsChildCollection ||
              item.TargetField === 'DocumentHeaderRowId'
            "
            @update:model-value="updateProperty(item, 'MappingFields.0.JSON.JsonPath', $event)" />

          <!-- Database properties -->
          <BaseInput
            v-else-if="mappingType === MappingType.Database && header === 'ColumnName'"
            class="h-8 bg-white rounded-md"
            color="light"
            :model-value="item.MappingFields[0]?.Database.ColumnName"
            :disabled="
              mode === 'view' ||
              item.IsChildCollection ||
              item.TargetField === 'DocumentHeaderRowId'
            "
            @update:model-value="
              updateProperty(item, 'MappingFields.0.Database.ColumnName', $event)
            " />

          <!-- LookupSettings properties -->
          <BaseToggle
            v-else-if="header === 'LookupIsEnabled'"
            class="flex-col"
            :name="`lookupEnabled-${item.TargetField || 'new'}`"
            :model-value="item.LookupSettings.IsEnabled"
            :disable="mode === 'view' || item.TargetField === 'DocumentHeaderRowId'"
            @update:model-value="updateProperty(item, 'LookupSettings.IsEnabled', $event)" />

          <BaseInput
            v-else-if="
              header === 'LookupTable' ||
              header === 'LookupKeyField' ||
              header === 'LookupResultField' ||
              header === 'LookupDescription' ||
              header === 'LookupCacheTimeout'
            "
            class="h-8 bg-white rounded-md"
            color="light"
            :model-value="item.LookupSettings[header]"
            :disabled="
              mode === 'view' ||
              !item.LookupSettings.IsEnabled ||
              item.TargetField === 'DocumentHeaderRowId'
            "
            @update:model-value="
              updateProperty(item, `LookupSettings.${header}`, $event, {
                requiresLookupEnabled: true,
              })
            " />

          <BaseToggle
            v-else-if="!!item.LookupSettings.ToValidate && header === 'ToValidateIsEnabled'"
            class="flex-col"
            :name="`toValidateIsEnabled-${item.TargetField || 'new'}`"
            :model-value="item.LookupSettings.ToValidate.IsEnabled"
            :disable="
              mode === 'view' ||
              !item.LookupSettings.IsEnabled ||
              item.TargetField === 'DocumentHeaderRowId'
            "
            @update:model-value="
              updateProperty(item, 'LookupSettings.ToValidate.IsEnabled', $event, {
                requiresLookupEnabled: true,
              })
            " />

          <BaseInput
            v-else-if="
              item.LookupSettings.ToValidate &&
              (header === 'LogTableName' || header === 'SourceField')
            "
            class="h-8 bg-white rounded-md"
            color="light"
            :model-value="item.LookupSettings.ToValidate[header]"
            :disabled="
              mode === 'view' ||
              !item.LookupSettings.IsEnabled ||
              item.TargetField === 'DocumentHeaderRowId'
            "
            @update:model-value="
              updateProperty(item, `LookupSettings.ToValidate.${header}`, $event, {
                requiresLookupEnabled: true,
              })
            " />

          <!-- ChildMappingSettings - ToTable properties -->
          <BaseInput
            v-else-if="header === 'RelationshipKey'"
            class="h-8 bg-white rounded-md"
            color="light"
            :model-value="item.ChildMappingSettings?.RelationshipKey || ''"
            :disabled="mode === 'view' || !item.IsChildCollection"
            @update:model-value="
              updateProperty(item, 'ChildMappingSettings.RelationshipKey', $event, {
                requiresChildCollection: true,
                initializeChildSettings: true,
              })
            " />

          <BaseInput
            v-else-if="header === 'TableName' || header === 'KeyField'"
            class="h-8 bg-white rounded-md"
            color="light"
            :model-value="item.ChildMappingSettings?.ToTable?.[header] || ''"
            :disabled="mode === 'view' || !item.IsChildCollection"
            @update:model-value="
              updateProperty(item, `ChildMappingSettings.ToTable.${header}`, $event, {
                requiresChildCollection: true,
                initializeChildSettings: true,
              })
            " />

          <!-- ChildMappingSettings - CSV properties -->
          <BaseInput
            v-else-if="
              mappingType === MappingType.CSV &&
              (header === 'StartColumnIndex' ||
                header === 'DetailSeparator' ||
                header === 'FieldSeparator')
            "
            class="h-8 bg-white rounded-md"
            color="light"
            :model-value="item.ChildMappingSettings?.CSV?.[header]"
            :disabled="mode === 'view' || !item.IsChildCollection"
            @update:model-value="
              updateProperty(item, `ChildMappingSettings.CSV.${header}`, $event, {
                requiresChildCollection: true,
                initializeChildSettings: true,
              })
            " />

          <!-- ChildMappingSettings - FixedLength properties -->
          <BaseInput
            v-else-if="
              mappingType === MappingType.FixedLength &&
              (header === 'StartPosition' ||
                header === 'DetailRecordLength' ||
                header === 'MaxDetails')
            "
            class="h-8 bg-white rounded-md"
            type="number"
            color="light"
            :model-value="item.ChildMappingSettings?.FixedLength?.[header]"
            :disabled="mode === 'view' || !item.IsChildCollection"
            @update:model-value="
              updateProperty(
                item,
                `ChildMappingSettings.FixedLength.${header}`,
                $event ? parseInt(String($event)) : null,
                { requiresChildCollection: true, initializeChildSettings: true }
              )
            " />

          <!-- ChildMappingSettings - JSON properties -->
          <BaseInput
            v-else-if="
              mappingType === MappingType.JSON && (header === 'ArrayPath' || header === 'ItemPath')
            "
            class="h-8 bg-white rounded-md"
            color="light"
            :model-value="item.ChildMappingSettings?.JSON?.[header] || ''"
            :disabled="mode === 'view' || !item.IsChildCollection"
            @update:model-value="
              updateProperty(item, `ChildMappingSettings.JSON.${header}`, $event, {
                requiresChildCollection: true,
                initializeChildSettings: true,
              })
            " />

          <!-- ChildMappingSettings - Database properties -->
          <BaseInput
            v-else-if="
              mappingType === MappingType.Database &&
              (header === 'JoinCondition' || header === 'GroupBy')
            "
            class="h-8 bg-white rounded-md"
            color="light"
            :model-value="item.ChildMappingSettings?.Database?.[header] || ''"
            :disabled="mode === 'view' || !item.IsChildCollection"
            @update:model-value="
              updateProperty(item, `ChildMappingSettings.Database.${header}`, $event, {
                requiresChildCollection: true,
                initializeChildSettings: true,
              })
            " />

          <!-- FilterConditions button -->
          <button
            v-else-if="header === 'FilterConditions'"
            type="button"
            :class="[
              'flex items-center gap-1 px-3 py-1 text-xs rounded-md transition-colors',
              !item.LookupSettings.IsEnabled
                ? 'text-gray-400 bg-gray-100 border border-gray-200'
                : 'text-blue-600 bg-blue-50 border border-blue-200 hover:bg-blue-100',
            ]"
            :disabled="!item.LookupSettings.IsEnabled"
            @click="openFilterConditionsModal(item)">
            <span
              >{{ (item.LookupSettings.FilterConditions || []).length }}
              {{ $t('representative.fieldMapping.filterConditions') }}</span
            >
          </button>

          <!-- RowMapping button -->
          <button
            v-else-if="header === 'RowMapping'"
            type="button"
            :class="[
              'flex items-center gap-1 px-3 py-1 text-xs rounded-md transition-colors',
              item.TargetField !== 'DocumentHeaderRowId'
                ? 'text-gray-400 bg-gray-100 border border-gray-200'
                : 'text-blue-600 bg-blue-50 border border-blue-200 hover:bg-blue-100',
            ]"
            :disabled="item.TargetField !== 'DocumentHeaderRowId'"
            @click="openRowMappingModal(item)">
            <span
              >{{
                item.TargetField === 'DocumentHeaderRowId' ? (item.MappingFields || []).length : 0
              }}
              {{ $t('representative.fieldMapping.rowMapping') }}</span
            >
          </button>
        </div>
      </template>
      <template #actions="{ item }">
        <div class="flex flex-col items-center gap-1">
          <button
            type="button"
            class="p-1.5 text-gray-500 rounded-full hover:bg-gray-100"
            @click="discardFieldMapping(item)"
            v-if="modifiedItems.has(item) && mode === 'edit'">
            <IconXmark class="w-4 h-4" />
          </button>
          <button
            type="button"
            class="p-1.5 text-red-400 rounded-full hover:bg-red-50"
            @click="deleteFieldMapping(item)"
            v-if="mode === 'edit' && !modifiedItems.has(item)">
            <IconTrashOutline class="w-4 h-4" />
          </button>
        </div>
      </template>
    </BaseTable>
    <!-- FilterConditions Modal -->
    <FilterConditionsModal
      v-if="showFilterConditionsModal"
      :filter-conditions="currentFieldMappingForFilter?.LookupSettings.FilterConditions || []"
      :mode="mode"
      @close="closeFilterConditionsModal"
      @save="saveFilterConditions" />

    <!-- RowMapping Modal -->
    <RowMappingModal
      v-if="showRowMappingModal"
      :mapping-fields="currentFieldMappingForRowMapping?.MappingFields || []"
      :mapping-type="mappingType"
      :mode="mode"
      @close="closeRowMappingModal"
      @save="saveRowMapping" />
  </div>
</template>

<script setup lang="ts">
import {
  eDataTypes,
  MappingType,
  type DataCollectionProperty,
  type FieldMapping,
} from '@/types/RecordMapping';
import type { SelectDropdownItem } from '@/types/Base';

import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import IconXmark from '@/assets/icons/IconXmark.vue';
import BaseInput from '@/components/common/BaseInput.vue';
import BaseSearchDropdown from '@/components/common/BaseSearchDropdown.vue';
import BaseTable from '@/components/common/BaseTable.vue';
import BaseToggle from '@/components/common/BaseToggle.vue';
import FilterConditionsModal from './FilterConditionsModal.vue';
import RowMappingModal from './RowMappingModal.vue';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToastStore } from '@/stores/toast';
import BaseSelectDropdown from '../common/BaseSelectDropdown.vue';
import { enumToDropdownItems } from '@/utils/enums';

const { t: $t } = useI18n();
const toastStore = useToastStore();

// v-model declarations
const fieldMappings = defineModel<FieldMapping[]>('fieldMappings', { required: true });
const selectedFieldMapping = defineModel<FieldMapping | undefined>('selectedFieldMapping');
const dropDownDataTypes = ref(enumToDropdownItems(eDataTypes));

const props = defineProps<{
  mode: 'view' | 'edit';
  mappingType: number;
  entityFields: DataCollectionProperty[];
}>();

function handleDataTypeSelection(item: SelectDropdownItem, fieldMapping: FieldMapping) {
  fieldMapping.selectedDataType = item ? item : null;
  updateProperty(fieldMapping, 'DataType', item.name || '');
}

function initializeSelectedDataTypeItems() {
  fieldMappings.value.forEach((item) => {
    if (item.DataType && !item.selectedDataType) {
      item.selectedDataType =
        dropDownDataTypes.value.find((dataType) => dataType.name === item.DataType) || null;
    }
  });
}

function getAvailableEntityFields(item: FieldMapping): SelectDropdownItem[] {
  const selectedFields = fieldMappings.value
    .filter((mapping) => mapping !== item && mapping.TargetField)
    .map((mapping) => mapping.TargetField!);

  return props.entityFields
    .filter((field) => !selectedFields.includes(field.propertyName))
    .map((field) => ({
      value: field.propertyName,
      name: field.propertyName,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

const fieldSearchInput = ref('');

// Reactive state for inline editing
const modifiedItems = ref<Set<FieldMapping>>(new Set());
const dropdownForceUpdate = ref(0);
const backupItems = ref<Map<FieldMapping, FieldMapping>>(new Map());
const editedFieldMapping = ref<FieldMapping | null>(null);

// BaseSearchDropdown functions
function getFilteredAvailableEntityFields(item: FieldMapping): SelectDropdownItem[] {
  const baseFields = getAvailableEntityFields(item);

  if (!fieldSearchInput.value) return baseFields;

  const searchTerm = fieldSearchInput.value.toLowerCase();
  return baseFields.filter(
    (field) =>
      field.name.toLowerCase().includes(searchTerm) ||
      (field.value as string).toLowerCase().includes(searchTerm)
  );
}

function updateFieldSearch(searchInput: string) {
  fieldSearchInput.value = searchInput;
}

function initializeSelectedTargetFieldItems() {
  fieldMappings.value.forEach((item) => {
    if (item.TargetField && !item.selectedTargetFieldItem) {
      const availableFields = getAvailableEntityFields(item);
      item.selectedTargetFieldItem =
        availableFields.find((field) => field.value === item.TargetField) || null;
    }
  });
}

watch(
  () => fieldMappings.value,
  () => {
    initializeSelectedTargetFieldItems();
    initializeSelectedDataTypeItems();
  },
  { immediate: true, deep: true }
);

const showFilterConditionsModal = ref(false);
const currentFieldMappingForFilter = ref<FieldMapping | null>(null);

const showRowMappingModal = ref(false);
const currentFieldMappingForRowMapping = ref<FieldMapping | null>(null);

const headers = computed(() => {
  const baseHeaders = [
    {
      value: 'TargetField',
      text: $t('representative.fieldMapping.targetField'),
      type: 'custom',
      active: true,
      hasInlineEdit: true,
    },
    {
      value: 'IsChildCollection',
      text: $t('representative.fieldMapping.isChildCollection'),
      type: 'check',
      active: true,
    },
    {
      value: 'DataType',
      text: $t('representative.fieldMapping.dataType'),
      type: 'custom',
      active: true,
      hasInlineEdit: true,
    },
    {
      value: 'IsRequired',
      text: $t('representative.fieldMapping.isRequired'),
      type: 'custom',
      active: true,
      hasInlineEdit: true,
    },
    {
      value: 'IsNullable',
      text: $t('representative.fieldMapping.isNullable'),
      type: 'custom',
      active: true,
      hasInlineEdit: true,
    },
    {
      value: 'DefaultValue',
      text: $t('representative.fieldMapping.defaultValue'),
      type: 'custom',
      active: true,
      hasInlineEdit: true,
    },
    {
      value: 'Description',
      text: $t('representative.fieldMapping.description'),
      type: 'custom',
      active: true,
      hasInlineEdit: true,
    },
    {
      value: 'ChildRecordType',
      text: $t('representative.fieldMapping.childRecordType'),
      type: 'custom',
      active: true,
      hasInlineEdit: true,
    },
  ];

  // Add MappingField columns based on mappingType
  const mappingFieldHeaders = [];
  if (props.mappingType === MappingType.CSV) {
    mappingFieldHeaders.push(
      {
        value: 'ColumnName',
        text: $t('representative.fieldMapping.csvColumnName'),
        type: 'custom',
        active: true,
        hasInlineEdit: true,
      },
      {
        value: 'ColumnIndex',
        text: $t('representative.fieldMapping.csvColumnIndex'),
        type: 'custom',
        active: true,
        hasInlineEdit: true,
      }
    );
  } else if (props.mappingType === MappingType.FixedLength) {
    mappingFieldHeaders.push(
      {
        value: 'StartPosition',
        text: $t('representative.fieldMapping.fixedLengthStartPosition'),
        type: 'custom',
        active: true,
        hasInlineEdit: true,
      },
      {
        value: 'Length',
        text: $t('representative.fieldMapping.fixedLengthLength'),
        type: 'custom',
        active: true,
        hasInlineEdit: true,
      }
    );
  } else if (props.mappingType === MappingType.JSON) {
    mappingFieldHeaders.push({
      value: 'JsonPath',
      text: $t('representative.fieldMapping.jsonPath'),
      type: 'custom',
      active: true,
      hasInlineEdit: true,
    });
  } else if (props.mappingType === MappingType.Database) {
    mappingFieldHeaders.push({
      value: 'ColumnName',
      text: $t('representative.fieldMapping.databaseColumnName'),
      type: 'custom',
      active: true,
      hasInlineEdit: true,
    });
  }

  // Add ChildMappingSettings columns based on mappingType
  const childMappingHeaders = [];
  // ToTable settings (always present for child collections)
  childMappingHeaders.push(
    {
      value: 'RelationshipKey',
      text: $t('representative.fieldMapping.childRelationshipKey'),
      type: 'custom',
      active: true,
      hasInlineEdit: true,
    },
    {
      value: 'TableName',
      text: $t('representative.fieldMapping.childToTableName'),
      type: 'custom',
      active: true,
      hasInlineEdit: true,
    },
    {
      value: 'KeyField',
      text: $t('representative.fieldMapping.childToKeyField'),
      type: 'custom',
      active: true,
      hasInlineEdit: true,
    }
  );

  if (props.mappingType === MappingType.CSV) {
    childMappingHeaders.push(
      {
        value: 'StartColumnIndex',
        text: $t('representative.fieldMapping.childCsvStartColumnIndex'),
        type: 'custom',
        active: true,
        hasInlineEdit: true,
      },
      {
        value: 'DetailSeparator',
        text: $t('representative.fieldMapping.childCsvDetailSeparator'),
        type: 'custom',
        active: true,
        hasInlineEdit: true,
      },
      {
        value: 'FieldSeparator',
        text: $t('representative.fieldMapping.childCsvFieldSeparator'),
        type: 'custom',
        active: true,
        hasInlineEdit: true,
      }
    );
  } else if (props.mappingType === MappingType.FixedLength) {
    childMappingHeaders.push(
      {
        value: 'StartPosition',
        text: $t('representative.fieldMapping.childFixedLengthStartPosition'),
        type: 'custom',
        active: true,
        hasInlineEdit: true,
      },
      {
        value: 'DetailRecordLength',
        text: $t('representative.fieldMapping.childFixedLengthDetailRecordLength'),
        type: 'custom',
        active: true,
        hasInlineEdit: true,
      },
      {
        value: 'MaxDetails',
        text: $t('representative.fieldMapping.childFixedLengthMaxDetails'),
        type: 'custom',
        active: true,
        hasInlineEdit: true,
      }
    );
  } else if (props.mappingType === MappingType.JSON) {
    childMappingHeaders.push(
      {
        value: 'ArrayPath',
        text: $t('representative.fieldMapping.childJsonArrayPath'),
        type: 'custom',
        active: true,
        hasInlineEdit: true,
      },
      {
        value: 'ItemPath',
        text: $t('representative.fieldMapping.childJsonItemPath'),
        type: 'custom',
        active: true,
        hasInlineEdit: true,
      }
    );
  } else if (props.mappingType === MappingType.Database) {
    childMappingHeaders.push(
      {
        value: 'JoinCondition',
        text: $t('representative.fieldMapping.childDatabaseJoinCondition'),
        type: 'custom',
        active: true,
        hasInlineEdit: true,
      },
      {
        value: 'GroupBy',
        text: $t('representative.fieldMapping.childDatabaseGroupBy'),
        type: 'custom',
        active: true,
        hasInlineEdit: true,
      }
    );
  }

  // Add LookupSettings columns (always present)
  const lookupHeaders = [
    {
      value: 'LookupIsEnabled',
      text: $t('representative.fieldMapping.lookupEnabled'),
      type: 'custom',
      active: true,
      hasInlineEdit: true,
    },
    {
      value: 'LookupTable',
      text: $t('representative.fieldMapping.lookupTable'),
      type: 'custom',
      active: true,
      hasInlineEdit: true,
    },
    {
      value: 'LookupKeyField',
      text: $t('representative.fieldMapping.keyField'),
      type: 'custom',
      active: true,
      hasInlineEdit: true,
    },
    {
      value: 'LookupResultField',
      text: $t('representative.fieldMapping.resultField'),
      type: 'custom',
      active: true,
      hasInlineEdit: true,
    },
    {
      value: 'ToValidateIsEnabled',
      text: $t('representative.fieldMapping.toValidateEnabled'),
      type: 'custom',
      active: true,
      hasInlineEdit: true,
    },
    {
      value: 'LogTableName',
      text: $t('representative.fieldMapping.toValidateLogTableName'),
      type: 'custom',
      active: true,
      hasInlineEdit: true,
    },
    {
      value: 'SourceField',
      text: $t('representative.fieldMapping.toValidateSourceField'),
      type: 'custom',
      active: true,
      hasInlineEdit: true,
    },
    {
      value: 'LookupDescription',
      text: $t('representative.fieldMapping.lookupDescription'),
      type: 'custom',
      active: true,
      hasInlineEdit: true,
    },
    {
      value: 'FilterConditions',
      text: $t('representative.fieldMapping.filterConditions'),
      type: 'custom',
      active: true,
      hasInlineEdit: false,
    },
    {
      value: 'RowMapping',
      text: $t('representative.fieldMapping.rowMapping'),
      type: 'custom',
      active: true,
      hasInlineEdit: false,
    },
  ];

  // Actions column
  const actionsHeader = {
    value: 'actions',
    text: $t('baseObject.actions'),
    active: true,
  };

  return [
    ...baseHeaders,
    ...mappingFieldHeaders,
    ...childMappingHeaders,
    ...lookupHeaders,
    actionsHeader,
  ];
});

// Helper functions for property updates
function createBackup(item: FieldMapping) {
  if (!backupItems.value.has(item)) {
    backupItems.value.set(item, JSON.parse(JSON.stringify(item)));
  }
}

function findParentFieldMapping(parentPropertyName: string): FieldMapping | null {
  return (
    fieldMappings.value.find(
      (mapping) => mapping.TargetField === parentPropertyName && mapping.IsChildCollection
    ) || null
  );
}

function initializeChildMappingSettings(item: FieldMapping) {
  if (!item.ChildMappingSettings) {
    item.ChildMappingSettings = {
      RelationshipKey: '',
      ToTable: { TableName: '', KeyField: '' },
      CSV: { StartColumnIndex: null, DetailSeparator: '', FieldSeparator: '' },
      FixedLength: { StartPosition: null, DetailRecordLength: null, MaxDetails: null },
      JSON: { ArrayPath: '', ItemPath: '' },
      Database: { JoinCondition: '', GroupBy: '' },
      RestApi: { JoinCondition: '', GroupBy: '' },
      XLXS: { JoinCondition: '', GroupBy: '' },
    };
  }
}

// Unified property update function
function updateProperty(
  item: FieldMapping,
  propertyPath: string,
  value: any,
  options: {
    requiresChildCollection?: boolean;
    requiresLookupEnabled?: boolean;
    mappingType?: string;
    initializeChildSettings?: boolean;
  } = {}
) {
  if (props.mode === 'view') return;
  if (options.requiresChildCollection && !item.IsChildCollection) return;
  if (options.requiresLookupEnabled && !item.LookupSettings?.IsEnabled) return;

  // Prevent inline editing of MappingFields[0] for DocumentHeaderRowId
  // MappingFields should only be edited through the RowMapping modal (which updates the entire array)
  if (item.TargetField === 'DocumentHeaderRowId' && propertyPath.startsWith('MappingFields.0.')) {
    return;
  }

  createBackup(item);
  if (options.initializeChildSettings) initializeChildMappingSettings(item);

  if (propertyPath === 'TargetField') {
    // Check if this is a dot notation property (child mapping)
    if (value && value.includes('.')) {
      const parsed = value.split('.');
      if (parsed?.length === 2) {
        if (!findParentFieldMapping(parsed[0])) {
          toastStore.warning({
            text: $t('representative.fieldMapping.errors.parentMappingRequired', {
              parentProperty: parsed[0],
            }),
            timeout: 8000,
          });

          // Clear the selection
          const index = fieldMappings.value.findIndex((mapping) => mapping === item);
          if (index >= 0) {
            item.TargetField = '';
            item.selectedTargetFieldItem = null;
            fieldMappings.value[index] = { ...item };
            dropdownForceUpdate.value++;
            modifiedItems.value.add(fieldMappings.value[index]);
          }
          return;
        }
      }
    } else {
      item.IsChildCollection =
        props.entityFields.find((x) => x.propertyName === value)?.isChildCollection || false;
    }

    // Update selectedTargetFieldItem to match the new TargetField value
    if (value) {
      const availableFields = getAvailableEntityFields(item);
      item.selectedTargetFieldItem = availableFields.find((field) => field.value === value) || null;

      // Clear MappingFields for DocumentHeaderRowId - user must add via RowMapping modal
      if (value === 'DocumentHeaderRowId') {
        item.MappingFields = [];
      }
    } else {
      item.selectedTargetFieldItem = null;
    }
  }

  // we cannot directly use the dot notated path to access the property, we have to build it part by part
  const parts = propertyPath.split('.');
  let current: any = item;
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i];
    // Handle array indices (e.g., '0' in 'MappingFields.0.CSV')
    if (!isNaN(Number(part))) {
      current = current[Number(part)];
    } else {
      current = current[part];
    }
  }
  current[parts[parts.length - 1]] = value;

  modifiedItems.value.add(item);
}

// Event handlers for inline editing
function onRowClick(fieldMapping: FieldMapping) {
  selectedFieldMapping.value = fieldMapping;
  editedFieldMapping.value = fieldMapping;
}

function discardFieldMapping(item: FieldMapping) {
  const backup = backupItems.value.get(item);
  if (backup) {
    // Restore from backup
    Object.assign(item, JSON.parse(JSON.stringify(backup)));
    backupItems.value.delete(item);
  } else {
    // If no backup exists, this is a new item - remove it
    const index = (fieldMappings.value as FieldMapping[]).findIndex((mapping) => mapping === item);
    if (index >= 0) {
      (fieldMappings.value as FieldMapping[]).splice(index, 1);
      fieldMappings.value = [...fieldMappings.value];
    }
  }
  modifiedItems.value.delete(item);

  if (selectedFieldMapping.value === item) {
    selectedFieldMapping.value = undefined;
  }
}

function deleteFieldMapping(item: FieldMapping) {
  const index = fieldMappings.value.findIndex((mapping) => mapping === item);
  if (index >= 0) {
    fieldMappings.value.splice(index, 1);
    fieldMappings.value = [...fieldMappings.value];
    if (selectedFieldMapping.value === item) {
      selectedFieldMapping.value = undefined;
    }
  }
  modifiedItems.value.delete(item);
  backupItems.value.delete(item);
}

function onFieldMappingAdd() {
  // Create a proper FieldMapping object with nested structure
  const defaultDataType = dropDownDataTypes.value.find((dt) => dt.name === 'string');
  const newMapping: FieldMapping = {
    TargetField: '',
    IsRequired: false,
    IsNullable: true,
    DataType: 'string',
    selectedDataType: defaultDataType || null,
    DefaultValue: '',
    Description: '',
    IsChildCollection: false,
    ChildRecordType: '',
    MappingFields: [
      {
        CSV: { ColumnIndex: null, ColumnName: '' },
        FixedLength: { StartPosition: null, Length: null },
        JSON: { JsonPath: '' },
        Database: { ColumnName: '' },
        RestApi: {},
        XLXS: { ColumnName: '' },
      },
    ],
    LookupSettings: {
      IsEnabled: false,
      LookupTable: '',
      LookupKeyField: '',
      LookupResultField: '',
      FilterConditions: [],
      LookupCacheResults: false,
      LookupCacheTimeout: null,
      LookupDescription: '',
      ToValidate: {
        IsEnabled: false,
        LogTableName: '',
        SourceField: '',
      },
    },
    ChildMappingSettings: null,
    ChildFieldMappings: null,
  };

  fieldMappings.value.unshift(newMapping);
  editedFieldMapping.value = fieldMappings.value[0];
  selectedFieldMapping.value = newMapping;

  modifiedItems.value.add(newMapping);
}

// FilterConditions modal functions
function openFilterConditionsModal(fieldMapping: FieldMapping) {
  currentFieldMappingForFilter.value = fieldMapping;
  showFilterConditionsModal.value = true;
}

function closeFilterConditionsModal() {
  showFilterConditionsModal.value = false;
  currentFieldMappingForFilter.value = null;
}

function saveFilterConditions(filterConditions: any[]) {
  if (currentFieldMappingForFilter.value) {
    if (!currentFieldMappingForFilter.value.LookupSettings) {
      currentFieldMappingForFilter.value.LookupSettings = {
        IsEnabled: false,
        LookupTable: '',
        LookupKeyField: '',
        LookupResultField: '',
        LookupCacheResults: false,
        LookupCacheTimeout: undefined,
        LookupDescription: '',
        FilterConditions: [],
        ToValidate: {
          IsEnabled: false,
          LogTableName: '',
          SourceField: '',
        },
      };
    }

    // Update FilterConditions
    currentFieldMappingForFilter.value.LookupSettings.FilterConditions = filterConditions;

    // Mark as modified for save/discard functionality
    updateProperty(
      currentFieldMappingForFilter.value,
      'LookupSettings.FilterConditions',
      filterConditions
    );
  }
  closeFilterConditionsModal();
}

// RowMapping modal functions
function openRowMappingModal(fieldMapping: FieldMapping) {
  currentFieldMappingForRowMapping.value = fieldMapping;
  showRowMappingModal.value = true;
}

function closeRowMappingModal() {
  showRowMappingModal.value = false;
  currentFieldMappingForRowMapping.value = null;
}

function saveRowMapping(mappingFields: any[]) {
  if (currentFieldMappingForRowMapping.value) {
    // Update MappingFields
    currentFieldMappingForRowMapping.value.MappingFields = mappingFields;

    // Mark as modified for save/discard functionality
    updateProperty(currentFieldMappingForRowMapping.value, 'MappingFields', mappingFields);
  }
  closeRowMappingModal();
}
</script>
